import {
  Checkbox,
  IconButton,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import DownloadIcon from "@material-ui/icons/GetApp";
import PromptDialog from "@saleor/components/PromptDialog/PromptDialog";
import TablePagination from "@saleor/components/TablePagination/TablePagination";
import { couponEditUnlockCodeUrl } from "@saleor/coupons/url";
import useNavigator from "@saleor/hooks/useNavigator";
import React, { FC, useCallback } from "react";

import { PROMPT_DIALOG_TEXTS } from "./constants";
import useDownloadQr from "./hooks/useDownloadQr";
import useSelectedIdsState from "./hooks/useSelectedIdsState";
import useUnlockCodeDeleteApi from "./hooks/useUnlockCodeDeleteApi";
import { useStyles } from "./styles";
import SelectedCountHeader from "./subcomponents/SelectedCountHeader/SelectedCountHeader";
export interface IUnlockCodeRow {
  id: string;
  unlockCode: string;
  usageLimit: number;
  description: string;
}

interface IUnlockCodesTableProps {
  couponId: string;
  unlockCodeData: IUnlockCodeRow[];
  onRowNumberChange: (rowNum: number) => void;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  rowNumber: number;
  onClickNextPage: () => void;
  onClickPrevPage: () => void;
  isCouponEditable: boolean;
  handleRefetch: () => void;
}

const UnlockCodesTable: FC<IUnlockCodesTableProps> = ({
  couponId,
  unlockCodeData,
  onRowNumberChange,
  hasNextPage,
  hasPrevPage,
  rowNumber,
  onClickNextPage,
  onClickPrevPage,
  isCouponEditable,
  handleRefetch
}) => {
  const classes = useStyles({});
  const navigate = useNavigator();

  const afterDeleteCallback = useCallback(() => {
    handleClearSelectedItems();
    handleRefetch();
  }, [handleRefetch]);

  const {
    selectedIds,
    checkboxHeaderState,
    handleSelectAllCheckbox,
    onRowCheckBoxClick,
    handleClearSelectedItems
  } = useSelectedIdsState(unlockCodeData);

  const {
    isPromptDialogShown,
    onDelete,
    onPromptProceed,
    onPromptCancel
  } = useUnlockCodeDeleteApi(afterDeleteCallback);

  const { downloadUnlockCodeQr } = useDownloadQr();

  const onEdit = useCallback(
    (unlockCodeIdToEdit: string) => () => {
      navigate(couponEditUnlockCodeUrl(couponId, unlockCodeIdToEdit));
    },
    [couponId]
  );

  const hasSelectedItems = selectedIds.size > 0;

  return (
    <div className={classes.container}>
      {selectedIds.size > 0 && (
        <SelectedCountHeader
          isCouponEditable={isCouponEditable}
          selectedCount={selectedIds.size}
          onDeleteClick={onDelete([...selectedIds])}
        />
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell className={classes.checkboxHeaderCell} align="left">
              <Checkbox
                indeterminate={checkboxHeaderState.isIndeterminate}
                checked={checkboxHeaderState.isChecked}
                onChange={handleSelectAllCheckbox}
              />
            </TableCell>
            <TableCell className={classes.unlockCodeHeaderCell} align="left">
              Unlock Code
            </TableCell>
            <TableCell className={classes.usageLimitHeaderCell} align="left">
              Usage Limit
            </TableCell>
            <TableCell className={classes.descriptionHeaderCell} align="left">
              Description
            </TableCell>
            <TableCell className={classes.actionsHeaderCell} align="right">
              Action(s)
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {unlockCodeData.map(unlockCode => (
            <TableRow key={unlockCode.id}>
              <TableCell className={classes.checkboxBodyCell} align="left">
                <Checkbox
                  checked={selectedIds.has(unlockCode.id)}
                  onChange={onRowCheckBoxClick(unlockCode.id)}
                />
              </TableCell>
              <TableCell className={classes.unlockCodeBodyCell} align="left">
                <Tooltip title={unlockCode.unlockCode} placement="bottom-start">
                  <div>
                    {unlockCode.unlockCode.length > 15
                      ? `${unlockCode.unlockCode.slice(0, 15)}...`
                      : unlockCode.unlockCode}
                  </div>
                </Tooltip>
              </TableCell>
              <TableCell className={classes.usageLimitBodyCell} align="left">
                {unlockCode.usageLimit > 0
                  ? unlockCode.usageLimit
                  : "Unlimited"}
              </TableCell>
              <TableCell className={classes.descriptionBodyCell} align="left">
                <Tooltip
                  title={unlockCode.description}
                  placement="bottom-start"
                >
                  <div>
                    {unlockCode.description.length > 25
                      ? `${unlockCode.description.slice(0, 25)}...`
                      : unlockCode.description}
                  </div>
                </Tooltip>
              </TableCell>
              <TableCell className={classes.actionsBodyCell} align="right">
                <Tooltip title="Download Unlock Code" placement="left">
                  <IconButton
                    color="primary"
                    onClick={downloadUnlockCodeQr(
                      unlockCode.id,
                      `${unlockCode.unlockCode}_${unlockCode.id}`
                    )}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                <IconButton
                  disabled={!isCouponEditable}
                  color="primary"
                  onClick={onEdit(unlockCode.id)}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  disabled={!isCouponEditable || hasSelectedItems}
                  color="primary"
                  onClick={onDelete([unlockCode.id])}
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              settings={{ rowNumber }}
              hasNextPage={hasNextPage}
              onNextPage={onClickNextPage}
              onUpdateListSettings={(key, val) => {
                if (key === "rowNumber") {
                  onRowNumberChange(val as number);
                }
              }}
              hasPreviousPage={hasPrevPage}
              onPreviousPage={onClickPrevPage}
            />
          </TableRow>
        </TableFooter>
      </Table>
      <PromptDialog
        isDialogShown={isPromptDialogShown}
        onProceed={onPromptProceed}
        onCancel={onPromptCancel}
        dialogTexts={PROMPT_DIALOG_TEXTS}
        isWarningTitle
      />
    </div>
  );
};

export default UnlockCodesTable;
